<script lang="ts">
export default {
  name: 'TermsAndConditionsModal',
  customOptions: {},
};
</script>

<script lang="ts" setup>
import { type PropType, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Locale } from 'vue-i18n';

import LocalTime from '@exchange/libs/components/history/LocalTime.vue';
import usePromise from '@exchange/libs/composables/shared/src/lib/usePromise';
import { modalVariant, type ModalVariant } from '@exchange/libs/modals/src';

const { locale } = useI18n();

type TermsMode = 'acknowledge' | 'accept';

const props = defineProps({
  accept: { type: Function as PropType<() => Promise<void>>, required: true },
  effectiveDate: { type: String, required: true },
  html: { type: Object as PropType<Record<Locale, string>>, required: false },
  mode: { type: String as PropType<TermsMode>, default: 'acknowledge' },
  remindMeLater: { type: Function as PropType<() => Promise<void>>, required: false },
  variant: { type: String as PropType<ModalVariant>, default: modalVariant.dark },
});

const { loading: accepting, createPromise: accept } = usePromise(props.accept);

const effectiveDate = computed(() => new Date(props.effectiveDate));
</script>

<template>
  <x-modal
    class="terms-and-conditions-modal"
    :can-close="false"
    :wide="true"
    :variant="variant"
    :title="$t('ui.termsAndConditions.title')"
  >
    <!-- Content -->
    <template #content>
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="html"
        :key="locale"
        class="terms-and-conditions-modal__content-html"
        v-html="html[locale]"
      />
      <!-- eslint-enable vue/no-v-html -->
      <i18n-t
        v-else
        keypath="ui.termsAndConditions.disclaimer"
        class="terms-and-conditions-modal__content"
        scope="global"
        tag="p"
      >
        <template #linkTermsAndConditions>
          <x-link-external
            :href="$t('ui.termsAndConditions.disclaimerLinks.linkTermsAndConditions.link')"
            :use-system-browser="true"
          >
            {{ $t('ui.termsAndConditions.disclaimerLinks.linkTermsAndConditions.text') }}
          </x-link-external>
        </template>
        <template #termsEffectiveDate>
          <local-time
            :time="effectiveDate"
            format="d MMMM yyyy"
          />
        </template>
        <template #linkCustomerCare>
          <x-link-external
            :href="$t('ui.termsAndConditions.disclaimerLinks.linkSupport.link')"
            :use-system-browser="true"
          >
            {{ $t('ui.termsAndConditions.disclaimerLinks.linkSupport.text') }}
          </x-link-external>
        </template>
      </i18n-t>
    </template>

    <!-- Actions -->
    <template #buttons>
      <x-button
        testid="accept-terms-and-conditions"
        variant="primary"
        :loading="accepting"
        :disabled="accepting"
        @click="accept"
      >
        {{ mode === 'acknowledge' ? $t('fundamentals.ok') : $t('fundamentals.accept') }}
      </x-button>
      <x-button
        v-if="mode === 'accept'"
        testid="remind-me-later-terms-and-conditions"
        variant="outline"
        @click="remindMeLater"
      >
        {{ $t('ui.termsAndConditions.remindMeLater') }}
      </x-button>
    </template>
  </x-modal>
</template>

<style lang="scss">
.terms-and-conditions-modal {
  &__content-html {
    a {
      color: var(--color-primary);
      font-weight: var(--font-weight-bold);

      &:hover {
        text-decoration: none;
      }
    }
  }
}
</style>
